import { useEffect, useState, type CSSProperties } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Calendar, Clock, Users, Instagram, ExternalLink, Minus, Plus, Send, Loader2, ArrowLeft, ImageOff } from "lucide-react";
import { eventosPublicosApi, EventoPublicoDetalle } from "../../lib/api";
import { aplicarFaviconPagina, aplicarTituloPagina } from "../../lib/branding";

function fmtFecha(s: string) {
  try {
    return new Date(s.slice(0, 10) + "T00:00:00").toLocaleDateString("es-VE", {
      weekday: "long", day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return s; }
}

export default function EventoDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [evento, setEvento] = useState<EventoPublicoDetalle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ nombre: "", telefono: "", email: "", cedula: "", cantidadEntradas: 1 });
  const [enviando, setEnviando] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    eventosPublicosApi.detalle(Number(id))
      .then((data) => {
        setEvento(data);
        aplicarFaviconPagina(data.logoUrl);
        aplicarTituloPagina(data.sucursalNombre ?? data.titulo);
      })
      .catch((e) => setError(e?.message ?? "Evento no encontrado"));
  }, [id]);

  const comprar = async () => {
    if (!evento) return;
    if (!form.nombre.trim()) { setFormError("Tu nombre es obligatorio"); return; }
    if (!form.telefono.trim()) { setFormError("Tu teléfono es obligatorio (ahí te avisamos)"); return; }
    setEnviando(true); setFormError(null);
    try {
      const r = await eventosPublicosApi.comprar(evento.id, {
        nombre: form.nombre.trim(),
        telefono: form.telefono.trim(),
        email: form.email.trim() || undefined,
        cedula: form.cedula.trim() || undefined,
        cantidadEntradas: form.cantidadEntradas,
      });
      navigate(`/eventos/compra/${r.compraId}`);
    } catch (e: any) {
      setFormError(e?.message ?? "No pudimos procesar la compra. Intentá de nuevo.");
    } finally {
      setEnviando(false);
    }
  };

  if (error) {
    return (
      <div className="evd-wrap"><style>{STYLES}</style>
        <div className="evd-error">{error}</div>
        <Link to="/eventos" className="evd-back"><ArrowLeft size={14} /> Ver otros eventos</Link>
      </div>
    );
  }

  if (!evento) {
    return <div className="evd-wrap"><style>{STYLES}</style><div className="evd-loading">Cargando…</div></div>;
  }

  const agotado = evento.cuposDisponibles <= 0;
  const noDisponible = evento.estado !== "publicado";
  const fondoEstilo: CSSProperties = evento.imagenFondoUrl
    ? {
        backgroundImage: `linear-gradient(rgba(246,241,231,0.88), rgba(246,241,231,0.88)), url(${evento.imagenFondoUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
      }
    : {};

  return (
    <div style={{ minHeight: "100vh", background: "#F6F1E7", ["--accent" as any]: evento.colorPrimario || "#3A6B8C", ...fondoEstilo }}>
      <style>{STYLES}</style>
      <div className="evd-wrap">
        <Link to="/eventos" className="evd-back"><ArrowLeft size={14} /> Ver otros eventos</Link>

        <div className="evd-card">
          <div className="evd-img">
            {evento.imagenUrl ? (
              <img src={evento.imagenUrl} alt={evento.titulo} />
            ) : (
              <div className="evd-img-fallback"><ImageOff size={36} /></div>
            )}
          </div>

          <div className="evd-body">
            <h1>{evento.titulo}</h1>
            <div className="evd-meta">
              <span><Calendar size={14} /> {fmtFecha(evento.fechaEvento)}</span>
              {evento.horaInicio && <span><Clock size={14} /> {evento.horaInicio.slice(0, 5)}{evento.horaFin ? ` – ${evento.horaFin.slice(0, 5)}` : ""}</span>}
              <span><Users size={14} /> {evento.cuposDisponibles} cupo(s) disponible(s)</span>
            </div>

            {evento.descripcion && <p className="evd-desc">{evento.descripcion}</p>}

            {evento.redSocialUrl && (
              <a href={evento.redSocialUrl} target="_blank" rel="noopener" className="evd-social">
                <Instagram size={15} /> Ver publicación original <ExternalLink size={13} />
              </a>
            )}

            <div className="evd-precio-banda">
              <span>Precio por entrada</span>
              <strong>USD {Number(evento.precioUsd).toFixed(2)}</strong>
            </div>

            {noDisponible ? (
              <div className="evd-no-disponible">Este evento ya no está disponible para la venta.</div>
            ) : agotado ? (
              <div className="evd-no-disponible">Las entradas de este evento están agotadas.</div>
            ) : (
              <div className="evd-compra">
                <h3>Reservá tu entrada</h3>
                <p className="evd-compra-sub">Tu cupo queda reservado hasta que reportemos tu pago (tenés {evento.horasLimitePago}h para hacerlo).</p>

                <div className="evd-cantidad">
                  <label>Cantidad de entradas</label>
                  <div className="evd-cantidad-ctrl">
                    <button type="button" onClick={() => setForm((f) => ({ ...f, cantidadEntradas: Math.max(1, f.cantidadEntradas - 1) }))}><Minus size={16} /></button>
                    <span>{form.cantidadEntradas}</span>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, cantidadEntradas: Math.min(evento.cuposDisponibles, f.cantidadEntradas + 1) }))}><Plus size={16} /></button>
                  </div>
                  <span className="evd-cantidad-total">Total: USD {(Number(evento.precioUsd) * form.cantidadEntradas).toFixed(2)}</span>
                </div>

                <div className="evd-grid">
                  <Field label="Tu nombre completo *">
                    <input className="evd-input" value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} />
                  </Field>
                  <Field label="Teléfono (WhatsApp) *">
                    <input className="evd-input" type="tel" value={form.telefono} onChange={(e) => setForm((f) => ({ ...f, telefono: e.target.value }))} />
                  </Field>
                  <Field label="Email (opcional)">
                    <input className="evd-input" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
                  </Field>
                  <Field label="Cédula (opcional)">
                    <input className="evd-input" value={form.cedula} onChange={(e) => setForm((f) => ({ ...f, cedula: e.target.value.toUpperCase() }))} placeholder="V12345678" />
                  </Field>
                </div>

                {formError && <div className="evd-form-error">{formError}</div>}

                <button onClick={comprar} disabled={enviando} className="evd-btn">
                  {enviando ? (<><Loader2 size={16} className="inline animate-spin" /> Procesando…</>) : (<><Send size={16} /> Reservar entrada(s)</>)}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="evd-field">
      <label>{label}</label>
      {children}
    </div>
  );
}

const STYLES = `
.evd-wrap { max-width: 680px; margin: 0 auto; padding: 20px 16px 48px; }
.evd-back { display: inline-flex; align-items: center; gap: 6px; color: var(--accent, #3A6B8C); font-size: 13px; text-decoration: none; margin-bottom: 14px; font-weight: 600; }
.evd-loading, .evd-error { text-align: center; color: #888; padding: 48px 16px; }
.evd-card { background: #fff; border-radius: 20px; overflow: hidden; box-shadow: 0 4px 24px rgba(0,0,0,0.08); border: 1px solid #ece6d8; }
.evd-img { width: 100%; height: 190px; background: #e9e4d8; }
@media (min-width: 640px) { .evd-img { height: 260px; } }
.evd-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.evd-img-fallback { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #b5ac97; }
.evd-body { padding: 22px 20px 26px; }
.evd-body h1 { font-family: Georgia, serif; font-size: 24px; font-weight: 700; color: #1c1c1c; margin: 0 0 10px; }
.evd-meta { display: flex; flex-wrap: wrap; gap: 14px; font-size: 13px; color: #666; margin-bottom: 14px; }
.evd-meta span { display: inline-flex; align-items: center; gap: 5px; }
.evd-desc { font-size: 14px; color: #444; line-height: 1.6; margin-bottom: 14px; white-space: pre-wrap; }
.evd-social { display: inline-flex; align-items: center; gap: 6px; color: #C13584; font-size: 13px; font-weight: 600; text-decoration: none; margin-bottom: 16px; }
.evd-precio-banda {
  display: flex; align-items: center; justify-content: space-between;
  background: #f6f1e7; border-radius: 12px; padding: 12px 16px; margin-bottom: 18px;
}
.evd-precio-banda span { font-size: 13px; color: #777; }
.evd-precio-banda strong { font-size: 20px; color: var(--accent, #3A6B8C); }
.evd-no-disponible { background: #fef3c7; color: #92400e; padding: 14px; border-radius: 12px; text-align: center; font-size: 14px; font-weight: 600; }
.evd-compra { border-top: 1px solid #ece6d8; padding-top: 18px; }
.evd-compra h3 { font-size: 17px; font-weight: 700; color: #1c1c1c; margin: 0 0 4px; }
.evd-compra-sub { font-size: 12.5px; color: #888; margin-bottom: 14px; }
.evd-cantidad { display: flex; flex-wrap: wrap; align-items: center; gap: 14px; margin-bottom: 16px; }
.evd-cantidad label { font-size: 12px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.4px; width: 100%; }
.evd-cantidad-ctrl { display: inline-flex; align-items: center; gap: 12px; background: #f6f1e7; border-radius: 10px; padding: 6px 14px; }
.evd-cantidad-ctrl button { width: 28px; height: 28px; border-radius: 8px; border: none; background: #fff; color: var(--accent, #3A6B8C); display: inline-flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
.evd-cantidad-ctrl span { font-weight: 700; min-width: 20px; text-align: center; }
.evd-cantidad-total { font-size: 13px; font-weight: 600; color: var(--accent, #3A6B8C); }
.evd-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 14px; }
@media (max-width: 480px) { .evd-grid { grid-template-columns: 1fr; } }
.evd-field label { display: block; font-size: 11px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.4px; margin-bottom: 4px; }
.evd-input { width: 100%; padding: 11px 13px; border-radius: 10px; border: 1.5px solid #d1d5db; font-size: 16px; font-family: inherit; }
.evd-input:focus { outline: 2px solid rgba(58,107,140,0.35); border-color: var(--accent, #3A6B8C); }
.evd-form-error { background: #fee2e2; color: #991b1b; padding: 10px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; }
.evd-btn {
  width: 100%; padding: 14px; background: linear-gradient(135deg, var(--accent, #3A6B8C), #2d5570); color: #fff;
  border-radius: 12px; font-weight: 700; font-size: 15px; border: none; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  box-shadow: 0 4px 12px rgba(58,107,140,0.3); min-height: 48px;
}
.evd-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.evd-btn .animate-spin { animation: evdSpin 0.9s linear infinite; }
@keyframes evdSpin { to { transform: rotate(360deg); } }
`;
